import AiExtractor from "../domain/AiExtractor";
import {ResourceOwnerType} from "../domain/ResourceOwnerType";

type AiExtractorOption = {
    label: string;
    value: string;
    managedByInterests: boolean;
};

type AiExtractorOptionGroup = {
    label: string;
    options: AiExtractorOption[];
};

export function buildAiExtractorSelectOptions(
    aiExtractors: AiExtractor[],
    isSystemAdmin: boolean,
    ownerFilter?: ResourceOwnerType.SYSTEM | ResourceOwnerType.ORGANIZATION,
    sourceOrganizationId?: string
): AiExtractorOption[] | AiExtractorOptionGroup[] {
    const toOption = (aiExtractor: AiExtractor): AiExtractorOption => ({
        label: aiExtractor.name ?? aiExtractor.id ?? "Unknown extractor",
        value: aiExtractor.id ?? "",
        managedByInterests: aiExtractor.managedByInterests === true
    });

    let filteredAiExtractors = ownerFilter
        ? aiExtractors.filter(aiExtractor => aiExtractor.owner === ownerFilter)
        : aiExtractors;

    if (sourceOrganizationId) {
        filteredAiExtractors = filteredAiExtractors.filter(aiExtractor =>
            aiExtractor.owner === ResourceOwnerType.SYSTEM || aiExtractor.organization?.id === sourceOrganizationId
        );
    }

    const systemAiExtractors = filteredAiExtractors.filter(
        aiExtractor => aiExtractor.owner === ResourceOwnerType.SYSTEM
    );
    const organizationAiExtractors = filteredAiExtractors.filter(
        aiExtractor => aiExtractor.owner === ResourceOwnerType.ORGANIZATION
    );

    if (!isSystemAdmin) {
        return [
            ...organizationAiExtractors.map(toOption),
            ...systemAiExtractors.map(toOption)
        ];
    }

    const organizationGroups = organizationAiExtractors.reduce<Record<string, AiExtractor[]>>(
        (groups, aiExtractor) => {
            const organizationName = aiExtractor.organization?.name ?? "Unknown";

            (groups[organizationName] ??= []).push(aiExtractor);

            return groups;
        },
        {}
    );

    return [
        ...(systemAiExtractors.length > 0 ? [{
            label: "System",
            options: systemAiExtractors.map(toOption)
        }] : []),
        ...Object.entries(organizationGroups).map(([organizationName, groupedAiExtractors]) => ({
            label: organizationName,
            options: groupedAiExtractors.map(toOption)
        }))
    ];
}
